import React, { useState } from 'react';
import { Box, Typography, Stack, Button, Paper, Divider, IconButton, TextField, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Chip, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import MapConfigurationSection from './MapConfigurationSection';
import ScheduleSection from './ScheduleSection';
import EditIcon from '../icons/EditIcon';

// Mobile-First Styled Components
const FormContainer = styled(Stack)(({ theme }) => ({
  gap: '20px',
  [theme.breakpoints.up('sm')]: {
    gap: '28px',
  },
  [theme.breakpoints.up('md')]: {
    gap: '40px',
  },
}));

const AccountSectionContainer = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  border: '0.6px solid #E2E8F0',
  padding: '16px',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  [theme.breakpoints.up('sm')]: {
    padding: '20px 24px',
  },
  [theme.breakpoints.up('md')]: {
    padding: '26px 30px',
  },
}));

const AccountRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: '16px',
  alignItems: 'stretch',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    gap: '30px',
    alignItems: 'flex-start',
  },
}));

const AccountColumn = styled(Stack)(({ theme }) => ({
  gap: '6px',
  flex: 1,
  padding: '12px 0',
  borderBottom: '1px solid #F1F5F9',
  '&:last-child': {
    borderBottom: 'none',
  },
  [theme.breakpoints.up('md')]: {
    padding: 0,
    borderBottom: 'none',
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '4px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '11px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '14px',
    fontWeight: 400,
    color: '#A0A0AA',
    textTransform: 'none',
    letterSpacing: 'normal',
  },
}));

const FieldValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#0f172a',
  lineHeight: 1.5,
  wordBreak: 'break-word',
  [theme.breakpoints.up('md')]: {
    fontSize: '14px',
    fontWeight: 400,
    color: '#121927',
  },
}));

const VerticalDivider = styled(Divider)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('md')]: {
    display: 'block',
    height: '95px',
    width: '1px',
    backgroundColor: '#F6F0F0',
  },
}));

const ContinueButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#0B91D6',
  color: '#ffffff',
  fontFamily: 'Inter, sans-serif',
  fontSize: '15px',
  fontWeight: 600,
  width: '100%',
  height: '52px',
  textTransform: 'capitalize',
  padding: '14px 24px',
  borderRadius: '10px',
  alignSelf: 'center',
  boxShadow: '0 4px 14px rgba(11, 145, 214, 0.25)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#0980C2',
    boxShadow: '0 6px 20px rgba(11, 145, 214, 0.35)',
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  [theme.breakpoints.up('sm')]: {
    width: '200px',
    fontSize: '14px',
    height: '48px',
  },
  [theme.breakpoints.up('md')]: {
    width: '160px',
  },
}));

const KeywordChipContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '6px',
  alignItems: 'center',
}));

const KeywordDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
}));

const ProfileStrengthForm = ({ formData, onFormChange, onContinue, isMobile, isTablet }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [keywordDialogOpen, setKeywordDialogOpen] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [keywords, setKeywords] = useState(
    (formData?.selectedKeywords ? String(formData.selectedKeywords).split(',').map(k => k.trim()).filter(Boolean) : [])
  );

  const openKeywordDialog = () => {
    // Sync chips from current value on open
    const list = (formData?.selectedKeywords ? String(formData.selectedKeywords).split(',').map(k => k.trim()).filter(Boolean) : []);
    setKeywords(list);
    setKeywordInput('');
    setKeywordDialogOpen(true);
  };

  const closeKeywordDialog = () => {
    setKeywordDialogOpen(false);
  };

  const addKeyword = () => {
    const val = keywordInput.trim();
    if (!val) return;
    if (!keywords.includes(val)) setKeywords(prev => [...prev, val]);
    setKeywordInput('');
  };

  const removeKeyword = (k) => {
    setKeywords(prev => prev.filter(x => x !== k));
  };

  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addKeyword();
    }
  };

  const saveKeywords = () => {
    const value = keywords.join(', ');
    if (typeof onFormChange === 'function') {
      onFormChange('selectedKeywords', value);
    }
    setKeywordDialogOpen(false);
  };

  // Truncate long text for mobile
  const truncateText = (text, maxLength = 40) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <FormContainer>
      {/* Account Selection Section */}
      <AccountSectionContainer elevation={0}>
        <AccountRow>
          <AccountColumn>
            <FieldLabel>Select Account*</FieldLabel>
            <FieldValue>
              {isSmallScreen ? truncateText(formData.selectedAccount, 35) : formData.selectedAccount}
            </FieldValue>
          </AccountColumn>

          <VerticalDivider orientation="vertical" />

          <AccountColumn>
            <FieldLabel>Selected Keyword*</FieldLabel>
            <KeywordDisplay>
              <FieldValue sx={{ flex: 1 }}>
                {isSmallScreen ? truncateText(formData.selectedKeywords, 30) : formData.selectedKeywords}
              </FieldValue>
              <Tooltip title="Edit keyword(s)">
                <IconButton
                  size="small"
                  onClick={openKeywordDialog}
                  sx={{
                    p: 0.75,
                    backgroundColor: '#F1F5F9',
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: '#E2E8F0',
                    },
                  }}
                >
                  <EditIcon width={14} height={14} />
                </IconButton>
              </Tooltip>
            </KeywordDisplay>
          </AccountColumn>

          <VerticalDivider orientation="vertical" />

          <AccountColumn>
            <FieldLabel>Location*</FieldLabel>
            <FieldValue sx={{ whiteSpace: 'pre-line' }}>
              {isSmallScreen ? truncateText(formData.location?.replace(/\n/g, ', '), 50) : formData.location}
            </FieldValue>
          </AccountColumn>
        </AccountRow>
      </AccountSectionContainer>

      {/* Keyword Editor Dialog - Fullscreen on mobile */}
      <Dialog
        open={keywordDialogOpen}
        onClose={closeKeywordDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '12px',
            margin: isMobile ? 0 : '32px',
          },
        }}
      >
        <DialogTitle sx={{
          fontSize: isMobile ? '18px' : '20px',
          fontWeight: 600,
          borderBottom: '1px solid #E2E8F0',
          pb: 2,
        }}>
          Edit Keywords
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Add keyword"
              placeholder="Type a keyword and press Enter"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={handleKeywordKeyDown}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            />
            <Box sx={{
              minHeight: '80px',
              p: 2,
              backgroundColor: '#F8FAFC',
              borderRadius: '10px',
              border: '1px solid #E2E8F0',
            }}>
              {keywords.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No keywords yet. Add one above.
                </Typography>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {keywords.map(k => (
                    <Chip
                      key={k}
                      label={k}
                      onDelete={() => removeKeyword(k)}
                      sx={{
                        mb: 1,
                        backgroundColor: '#0B91D6',
                        color: '#fff',
                        '& .MuiChip-deleteIcon': {
                          color: 'rgba(255,255,255,0.7)',
                          '&:hover': {
                            color: '#fff',
                          },
                        },
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
            <Button
              variant="outlined"
              size="medium"
              onClick={addKeyword}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Add Keyword
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{
          p: 2,
          pt: 0,
          gap: 1,
          borderTop: '1px solid #E2E8F0',
          mt: 2,
        }}>
          <Button
            onClick={closeKeywordDialog}
            sx={{
              flex: isMobile ? 1 : 'none',
              borderRadius: '8px',
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveKeywords}
            sx={{
              flex: isMobile ? 1 : 'none',
              borderRadius: '8px',
              textTransform: 'none',
              backgroundColor: '#0B91D6',
              '&:hover': {
                backgroundColor: '#0980C2',
              },
            }}
          >
            Save Keywords
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map Configuration Section */}
      <MapConfigurationSection
        formData={formData}
        onFormChange={onFormChange}
        isMobile={isMobile}
        isTablet={isTablet}
      />

      {/* Schedule Section */}
      <ScheduleSection
        formData={formData}
        onFormChange={onFormChange}
        isMobile={isMobile}
        isTablet={isTablet}
      />

      {/* Continue Button */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        pt: isMobile ? 1 : 2,
        pb: isMobile ? 2 : 0,
      }}>
        <ContinueButton onClick={onContinue}>
          Continue
        </ContinueButton>
      </Box>
    </FormContainer>
  );
};

export default ProfileStrengthForm;
